import { useEffect, useState } from 'react';

/**
 * useReducedMotion — returns whether the user prefers reduced motion.
 *
 * Reads `(prefers-reduced-motion: reduce)` via `matchMedia` and subscribes to
 * runtime changes, so toggling the OS setting while the page is open is honored
 * (an improvement over the previous snapshot-only check inline in
 * TypewriterTagline).
 *
 * SSR / no-matchMedia safe: when `window` or `window.matchMedia` is unavailable
 * the hook returns `false` and never subscribes.
 */

const QUERY = '(prefers-reduced-motion: reduce)';

function getInitialPreference() {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return window.matchMedia(QUERY).matches;
}

export default function useReducedMotion() {
  const [prefersReduced, setPrefersReduced] = useState(getInitialPreference);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;

    const mediaQuery = window.matchMedia(QUERY);
    const handleChange = (event) => setPrefersReduced(event.matches);

    // Sync once on mount in case the preference changed between the initial
    // render and the effect running.
    setPrefersReduced(mediaQuery.matches);

    // Modern browsers expose addEventListener; older Safari only has the
    // deprecated addListener/removeListener pair.
    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    }

    mediaQuery.addListener(handleChange);
    return () => mediaQuery.removeListener(handleChange);
  }, []);

  return prefersReduced;
}
